import { api } from './api';

// ============================================================================
// VEXA SERVICE - Meeting Bot Integration (Teams, Google Meet, Zoom)
// ============================================================================

export type VexaPlatform = 'teams' | 'google_meet' | 'zoom';

export interface DeployBotOptions {
  recordingEnabled?: boolean;
  transcribeEnabled?: boolean;
}

export interface BotDeployment {
  id: number;
  platform: string;
  native_meeting_id: string;
  status: string;
}

export interface BotStatus {
  status: 'connecting' | 'joined' | 'transcribing' | 'disconnected' | 'failed';
  duration?: number;
}

export interface TranscriptSegment {
  speaker: string;
  text: string;
  timestamp: string;
  confidence?: number;
}

export async function deployBot(
  platform: VexaPlatform,
  nativeMeetingId: string,
  passcode?: string,
  botName?: string,
  options?: DeployBotOptions,
): Promise<BotDeployment> {
  const res = await api.post('/api/vexa/bot/deploy', {
    platform,
    nativeMeetingId,
    passcode: passcode || undefined,
    botName: botName || undefined,
    recordingEnabled: options?.recordingEnabled,
    transcribeEnabled: options?.transcribeEnabled,
  });
  return res.data?.data || res.data;
}

export async function removeBotFromMeeting(platform: string, meetingId: string): Promise<void> {
  await api.delete(`/api/vexa/bot/${encodeURIComponent(platform)}/${encodeURIComponent(meetingId)}`);
}

export interface TranscriptAndStatus {
  status: string;
  segments: TranscriptSegment[];
}

/**
 * Single call that returns both bot status and transcript segments.
 * The Vexa transcript endpoint includes the bot status in the response.
 */
export async function getTranscriptAndStatus(platform: string, meetingId: string): Promise<TranscriptAndStatus> {
  const res = await api.get(`/api/vexa/bot/${encodeURIComponent(platform)}/${encodeURIComponent(meetingId)}/transcript`);
  return res.data?.data || res.data;
}

export async function botSpeak(platform: string, meetingId: string, options: {
  audioUrl?: string;
  audioBase64?: string;
  format?: string;
  text?: string;
}): Promise<void> {
  await api.post(`/api/vexa/bot/${encodeURIComponent(platform)}/${encodeURIComponent(meetingId)}/speak`, options);
}

export async function botChat(platform: string, meetingId: string, message: string): Promise<void> {
  await api.post(`/api/vexa/bot/${encodeURIComponent(platform)}/${encodeURIComponent(meetingId)}/chat`, { message });
}
